import * as React from "react";
import { createPortal } from "react-dom";
import { DayPicker } from "react-day-picker";
import { Calendar, ChevronLeft, ChevronRight, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { formatFullDay, parseValue, toLocalDate, todayIn, toValue } from "@/lib/dates";
import { usePopover } from "./usePopover";

/**
 * One day. `DateRangeFilter`'s single-value sibling, sharing its calendar
 * styling and `usePopover`'s placement.
 *
 * Not `<input type="date">`, and the reason is not decoration: the native
 * control renders in the OS's locale and its own colours, it cannot be told
 * which time zone "today" means, and on Firefox for desktop it has no picker at
 * all. `min`/`max` here are calendar dates in the same zone as the value, so
 * "not in the future" means the same thing to this control as it does to the
 * site's records.
 *
 * ── Not wired to anything yet ──────────────────────────────────────────────
 *
 * There is no single-date field in the console today. It is built alongside
 * `DateRangeFilter` because they share every piece of calendar styling, and
 * writing the second one later means either duplicating that or refactoring the
 * first under deadline.
 */
export function DatePicker({
  value,
  onChange,
  label = "Select a date",
  min,
  max,
  timeZone,
  clearable = true,
  invalid,
  disabled,
  id,
  className,
}: {
  /** "YYYY-MM-DD", or "" for no date. */
  value: string;
  onChange: (value: string) => void;
  label?: string;
  /** "YYYY-MM-DD" bounds, inclusive. */
  min?: string;
  max?: string;
  /** Whose "today" the shortcut means. The site's, where there is one. */
  timeZone?: string | null;
  clearable?: boolean;
  invalid?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
}) {
  const { open, toggle, close, rootRef, triggerRef, panelRef, panelStyle, panelTarget } =
    usePopover({
      align: "start",
    });

  const selected = parseValue(value);
  const selectedDate = selected ? toLocalDate(selected) : undefined;
  const minDate = parseValue(min ?? "");
  const maxDate = parseValue(max ?? "");
  const today = todayIn(timeZone);

  const bounds = [
    ...(minDate ? [{ before: toLocalDate(minDate) }] : []),
    ...(maxDate ? [{ after: toLocalDate(maxDate) }] : []),
  ];

  // "Today" is pointless, and would set a value the caller has said is invalid,
  // when today falls outside the allowed window - a "from" date capped by an
  // earlier "to", say.
  const todayValue = toValue(today);
  const todayOutOfRange =
    (min !== undefined && todayValue < min) || (max !== undefined && todayValue > max);

  function commit(next: string) {
    onChange(next);
    close(true);
  }

  return (
    <div ref={rootRef} className={cn("fsel", "fsel--field", className)}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        onClick={toggle}
        className={cn("fsel__trigger", "fsel__trigger--field", invalid && "fsel__trigger--invalid")}
      >
        <span className="fsel__icon">
          <Calendar />
        </span>
        <span className={cn("fsel__value", !selectedDate && "fsel__value--placeholder")}>
          {selectedDate ? formatFullDay(selectedDate) : label}
        </span>
      </button>

      {/*
        A sibling of the trigger, never a child of it. A `<button>` inside a
        `<button>` is invalid HTML that browsers silently restructure, and the
        usual workaround - a `<span role="button">` - is unreachable by keyboard.
      */}
      {clearable && selectedDate && !disabled && (
        <button
          type="button"
          aria-label="Clear the date"
          onClick={() => onChange("")}
          className="dpick__clear"
        >
          <X aria-hidden />
        </button>
      )}

      {open &&
        createPortal(
          <div
            ref={panelRef}
            // See the "top layer" note in `usePopover.ts` - this is what lets
            // the panel render over an open `Modal`/`Drawer`.
            popover="manual"
            role="dialog"
            aria-label={label}
            style={panelStyle}
            className="fsel__panel dpick__panel"
          >
            <DayPicker
              mode="single"
              selected={selectedDate}
              month={undefined}
              defaultMonth={selectedDate ?? toLocalDate(today)}
              disabled={bounds.length > 0 ? bounds : undefined}
              showOutsideDays
              onSelect={(day) => day && commit(toValue(day))}
              formatters={{
                formatWeekdayName: (day) =>
                  new Intl.DateTimeFormat(undefined, { weekday: "narrow" }).format(day),
              }}
              components={{
                Chevron: ({ orientation }) =>
                  orientation === "left" ? <ChevronLeft /> : <ChevronRight />,
              }}
            />

            <div className="dpick__footer">
              <button
                type="button"
                disabled={todayOutOfRange}
                onClick={() => commit(todayValue)}
                className="dpick__footer-btn"
              >
                Today
              </button>
              {clearable && selectedDate && (
                <button
                  type="button"
                  onClick={() => commit("")}
                  className="dpick__footer-btn dpick__footer-btn--ghost"
                >
                  Clear
                </button>
              )}
            </div>
          </div>,
          panelTarget
        )}
    </div>
  );
}
